import React, { useMemo, useState, memo } from 'react';
import { X, Shield, Clock, CheckCircle, AlertTriangle, Download, Server, Lock, Users, Search, ChevronDown, ChevronUp, Cloud, Database, Key, Eye, Wifi, HardDrive, FileWarning, ShieldAlert, UserX, Network, Bot } from 'lucide-react';
interface Finding {
  id: string;
  title: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  resource: string;
  category: string;
  description: string;
}
interface Scan {
  id: string;
  name: string;
  clientName?: string;
  target: string;
  status: string;
  started: string;
  duration: string;
  findings: {
    high: number;
    medium: number;
    low: number;
  };
}
interface ScanResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  scan: Scan | null;
}
// ─── Finding Templates ───
const CRITICAL_FINDINGS = [{
  title: 'Root Account Access Keys Active',
  category: 'Identity & Access',
  desc: 'AWS root account has active access keys. Root access keys should be deleted and IAM users with appropriate permissions used instead.'
}, {
  title: 'Over-Privileged Service Principal — Owner Role',
  category: 'Identity & Access',
  desc: 'Service principal has Owner role at subscription scope. Scope down to specific resource groups with least-privilege roles.'
}, {
  title: 'MFA Not Enforced for Admin Accounts',
  category: 'Authentication',
  desc: 'Admin accounts lack MFA enforcement via Conditional Access policy. This exposes the tenant to credential-based attacks.'
}, {
  title: 'Unrotated API Keys (180+ days)',
  category: 'Credential Management',
  desc: 'API keys have not been rotated in over 180 days, far exceeding the 90-day recommended rotation policy.'
}, {
  title: 'Public S3 Bucket with Sensitive Data',
  category: 'Data Protection',
  desc: 'S3 bucket containing PII/PHI data has public read access enabled. Immediate remediation required.'
}, {
  title: 'Wildcard IAM Policy Attached to Role',
  category: 'Identity & Access',
  desc: 'IAM role has a policy with Action: "*" and Resource: "*", granting unrestricted access to all AWS services.'
}, {
  title: 'Database Instance Publicly Accessible',
  category: 'Network Security',
  desc: 'RDS/SQL instance is publicly accessible with no IP restrictions. Restrict to VPC-only access.'
}, {
  title: 'Service Account with Domain Admin Privileges',
  category: 'Identity & Access',
  desc: 'Service account has been granted Domain Admin privileges. Apply least-privilege principle immediately.'
}, {
  title: 'Unencrypted Storage Volume with Customer Data',
  category: 'Data Protection',
  desc: 'EBS/Managed Disk volume containing customer data lacks encryption at rest. Enable default encryption.'
}, {
  title: 'Cross-Account Role Trust Too Broad',
  category: 'Identity & Access',
  desc: 'Cross-account IAM role trusts all principals in the external account. Restrict to specific roles/users.'
}, {
  title: 'Legacy TLS 1.0/1.1 Enabled on Load Balancer',
  category: 'Network Security',
  desc: 'Application load balancer accepts TLS 1.0 and 1.1 connections. Enforce TLS 1.2+ minimum.'
}, {
  title: 'Orphaned Admin Service Account — No Owner',
  category: 'Identity & Access',
  desc: 'Admin-level service account has no assigned owner and last activity was 240+ days ago.'
}, {
  title: 'Security Group Allows 0.0.0.0/0 on SSH (22)',
  category: 'Network Security',
  desc: 'Security group allows inbound SSH from any IP address. Restrict to bastion host or VPN CIDR only.'
}, {
  title: 'Key Vault Access Policy Grants All Permissions',
  category: 'Credential Management',
  desc: 'Key Vault access policy grants Get, List, Set, Delete on all secret/key/certificate operations to a broad principal.'
}, {
  title: 'IAM User with Console Access and No MFA',
  category: 'Authentication',
  desc: 'IAM user has console login enabled but no MFA device configured. Enforce MFA for all console users.'
}, {
  title: 'Stale Break-Glass Account Credentials',
  category: 'Credential Management',
  desc: 'Emergency break-glass account credentials have not been rotated in 365+ days and lack monitoring alerts.'
}, {
  title: 'Container Running as Root with Host Network',
  category: 'Workload Security',
  desc: 'Container workload runs as root user with host network access, enabling potential container escape.'
}, {
  title: 'Unrestricted Egress to Internet from Database Subnet',
  category: 'Network Security',
  desc: 'Database subnet has unrestricted outbound internet access. Implement NAT gateway with restricted egress rules.'
}, {
  title: 'Unregistered AI Agent with Admin-Level API Access',
  category: 'Shadow AI & Agentic Access',
  desc: 'An unregistered autonomous AI agent was discovered holding admin-level API keys provisioned outside change management. The agent has read/write access to production resources and is not tracked in the service catalog. Immediate revocation and audit trail review required.'
}, {
  title: 'Shadow LLM Service Account with Unrestricted Data Store Access',
  category: 'Shadow AI & Agentic Access',
  desc: 'A machine identity linked to a large language model integration has unrestricted access to S3/Blob storage containing PII and financial data. This service account was provisioned without security review and bypasses DLP controls.'
}, {
  title: 'Rogue AI Application with Tenant-Wide OAuth Consent Grant',
  category: 'Shadow AI & Agentic Access',
  desc: 'An unauthorized third-party AI application has been granted admin-consented OAuth permissions across the entire tenant, including Mail.ReadWrite, Files.ReadWrite.All, and User.Read.All. No record of approval exists in the governance workflow.'
}];
const MEDIUM_FINDINGS = [{
  title: 'Storage Account Public Access Enabled',
  category: 'Network Security',
  desc: 'Blob public access is enabled on storage account. Disable unless explicitly required for specific containers.'
}, {
  title: 'NSG Rule Allows Broad Inbound Access',
  category: 'Network Security',
  desc: 'Inbound rule allows traffic from 0.0.0.0/0 on port 443. Consider restricting to known IP ranges or WAF.'
}, {
  title: 'Diagnostic Logging Disabled on Key Vault',
  category: 'Monitoring',
  desc: 'Key Vault does not have diagnostic logging enabled. Enable audit logs for compliance and incident response.'
}, {
  title: 'S3 Bucket Versioning Disabled',
  category: 'Data Protection',
  desc: 'Versioning is disabled on data bucket. Enable to protect against accidental deletion and support recovery.'
}, {
  title: 'CloudTrail Not Multi-Region',
  category: 'Monitoring',
  desc: 'CloudTrail is only configured for primary region. Enable multi-region to capture all API activity globally.'
}, {
  title: 'Service Account Key Not Rotated (90+ days)',
  category: 'Credential Management',
  desc: 'Service account key created 90+ days ago. Implement automated key rotation via CI/CD pipeline.'
}, {
  title: 'Firewall Rule Too Permissive',
  category: 'Network Security',
  desc: 'Firewall rule allows all internal traffic across all ports. Restrict to required ports and protocols only.'
}, {
  title: 'GuardDuty/Defender Not Enabled',
  category: 'Monitoring',
  desc: 'Threat detection service is not enabled in this region/subscription. Enable for real-time threat monitoring.'
}, {
  title: 'IAM Password Policy Below Recommended Standards',
  category: 'Authentication',
  desc: 'Password policy allows passwords under 14 characters and does not require special characters.'
}, {
  title: 'Unused Security Group with Broad Rules',
  category: 'Network Security',
  desc: 'Security group has no attached resources but contains permissive inbound rules. Delete or restrict.'
}, {
  title: 'Lambda/Function Execution Role Over-Scoped',
  category: 'Identity & Access',
  desc: 'Serverless function execution role has broader permissions than required. Apply function-specific policies.'
}, {
  title: 'VPC Flow Logs Not Enabled',
  category: 'Monitoring',
  desc: 'VPC flow logs are disabled. Enable to capture network traffic metadata for security analysis.'
}, {
  title: 'Default VPC In Use for Production Workloads',
  category: 'Network Security',
  desc: 'Production workloads are deployed in the default VPC. Migrate to a custom VPC with proper segmentation.'
}, {
  title: 'Backup Encryption Not Enabled',
  category: 'Data Protection',
  desc: 'Automated backups are not encrypted. Enable encryption for backups to meet compliance requirements.'
}, {
  title: 'Access Logging Disabled on Load Balancer',
  category: 'Monitoring',
  desc: 'Application load balancer does not have access logging enabled. Enable for audit and forensic purposes.'
}, {
  title: 'Conditional Access Policy Gap — Unmanaged Devices',
  category: 'Authentication',
  desc: 'No Conditional Access policy restricts access from unmanaged devices to sensitive applications.'
}, {
  title: 'Service Principal with Stale Credentials',
  category: 'Credential Management',
  desc: 'Service principal has credentials that expire in 30+ days but have not been used in 60+ days.'
}, {
  title: 'Cross-Region Replication Not Configured',
  category: 'Data Protection',
  desc: 'Critical data stores lack cross-region replication. Configure for disaster recovery compliance.'
}, {
  title: 'WAF Not Configured on Public-Facing Application',
  category: 'Network Security',
  desc: 'Public-facing web application lacks Web Application Firewall protection against OWASP Top 10.'
}, {
  title: 'Secrets Stored in Environment Variables',
  category: 'Credential Management',
  desc: 'Application configuration contains secrets in plain-text environment variables. Migrate to secrets manager.'
}, {
  title: 'Network Watcher Not Enabled in All Regions',
  category: 'Monitoring',
  desc: 'Network Watcher is not enabled in all active regions. Enable for network diagnostics and monitoring.'
}, {
  title: 'EBS/Disk Snapshots Shared Publicly',
  category: 'Data Protection',
  desc: 'Disk snapshots are shared with all AWS accounts. Restrict sharing to specific trusted accounts.'
}, {
  title: 'Inactive User Account with Active Permissions',
  category: 'Identity & Access',
  desc: 'User account has not logged in for 60+ days but retains active permissions. Disable or remove.'
}, {
  title: 'Missing Resource Tags for Cost/Security Attribution',
  category: 'Governance',
  desc: 'Multiple resources lack required tags (Owner, Environment, CostCenter). Enforce tagging policy.'
}, {
  title: 'AI Copilot Plugin with Excessive Graph API Permissions',
  category: 'Shadow AI & Agentic Access',
  desc: 'A Microsoft 365 Copilot plugin has been granted Mail.ReadWrite, Sites.ReadWrite.All, and Chat.ReadWrite permissions via admin consent. The plugin was installed by a non-admin user and bypassed the standard app governance review process.'
}, {
  title: 'Agentic Workflow Service with Cross-Account IAM Role',
  category: 'Shadow AI & Agentic Access',
  desc: 'An AI orchestration platform (e.g., LangChain agent, AutoGPT) has assumed a cross-account IAM role with broad permissions. The role trust policy does not restrict to specific agent identities, allowing any principal from the agent platform account to assume it.'
}, {
  title: 'Unmonitored LLM Gateway Routing PII Without DLP',
  category: 'Shadow AI & Agentic Access',
  desc: 'An API gateway proxying requests to external LLM providers (OpenAI, Anthropic) is transmitting customer PII and financial data without data loss prevention controls. No audit logging is configured for prompt/response content.'
}, {
  title: 'Shadow GPT Integration via Employee-Provisioned API Keys',
  category: 'Shadow AI & Agentic Access',
  desc: 'Multiple API keys for third-party AI services (OpenAI, Anthropic, Cohere) were discovered in application environment variables, provisioned by individual employees outside the procurement process. Keys have access to production data pipelines.'
}, {
  title: 'AI Agent Service Account with Autonomous Resource Provisioning',
  category: 'Shadow AI & Agentic Access',
  desc: 'An autonomous AI agent holds IAM permissions to create, modify, and delete cloud resources (EC2, Lambda, S3) without human approval gates. The agent has provisioned 14 resources in the last 30 days with no change management records.'
}];
const LOW_FINDINGS = [{
  title: 'CloudWatch Log Group Retention Not Set',
  category: 'Monitoring',
  desc: 'Log group has indefinite retention. Set appropriate retention period to manage costs and compliance.'
}, {
  title: 'EC2 Instance Using Previous Generation Type',
  category: 'Governance',
  desc: 'Instance is running on a previous-generation instance type. Consider upgrading for better performance and cost.'
}, {
  title: 'SSL Certificate Expiring in 30 Days',
  category: 'Credential Management',
  desc: 'SSL/TLS certificate will expire within 30 days. Renew or enable auto-renewal to prevent service disruption.'
}, {
  title: 'Resource Group Naming Convention Inconsistency',
  category: 'Governance',
  desc: 'Resource group does not follow the established naming convention. Rename for consistency and automation.'
}, {
  title: 'Unused Elastic IP Address',
  category: 'Governance',
  desc: 'Elastic IP is allocated but not associated with any running instance. Release to avoid unnecessary charges.'
}, {
  title: 'Default Encryption Key Used Instead of CMK',
  category: 'Data Protection',
  desc: 'Service uses AWS/Azure managed encryption key. Consider using a customer-managed key for additional control.'
}, {
  title: 'Auto-Scaling Not Configured for Compute Resources',
  category: 'Governance',
  desc: 'Compute resources lack auto-scaling configuration. Configure to optimize cost and availability.'
}, {
  title: 'DNS Zone Transfer Not Restricted',
  category: 'Network Security',
  desc: 'DNS zone allows unrestricted zone transfers. Restrict to authorized secondary DNS servers only.'
}, {
  title: 'Idle Load Balancer Detected',
  category: 'Governance',
  desc: 'Load balancer has no registered targets or zero traffic in 14+ days. Remove if no longer needed.'
}, {
  title: 'CloudFormation/ARM Template Drift Detected',
  category: 'Governance',
  desc: 'Infrastructure configuration has drifted from the declared template. Reconcile to maintain consistency.'
}, {
  title: 'Untagged Network Interface',
  category: 'Governance',
  desc: 'Network interface lacks identifying tags. Apply tags for inventory management and cost tracking.'
}, {
  title: 'SNS Topic Without Encryption',
  category: 'Data Protection',
  desc: 'SNS topic does not have server-side encryption enabled. Enable for data-in-transit protection.'
}, {
  title: 'Deprecated API Version in Use',
  category: 'Governance',
  desc: 'Application is using a deprecated API version. Upgrade to the latest supported version.'
}, {
  title: 'Missing Health Check on Target Group',
  category: 'Monitoring',
  desc: 'Target group health check is using default settings. Configure application-specific health check path.'
}, {
  title: 'Route Table Contains Overly Broad Route',
  category: 'Network Security',
  desc: 'Route table has a broad route that could be narrowed. Review and restrict to required CIDR blocks.'
}, {
  title: 'Managed Identity Not Used for Azure Service',
  category: 'Identity & Access',
  desc: 'Azure service authenticates with stored credentials instead of managed identity. Migrate for better security.'
}, {
  title: 'Activity Log Alert Not Configured',
  category: 'Monitoring',
  desc: 'No activity log alerts configured for critical operations (policy changes, role assignments). Set up alerts.'
}, {
  title: 'Unused IAM Role (90+ days inactive)',
  category: 'Identity & Access',
  desc: 'IAM role has not been assumed in 90+ days. Review and remove if no longer needed to reduce attack surface.'
}, {
  title: 'AI Assistant Browser Extension with SSO Token Access',
  category: 'Shadow AI & Agentic Access',
  desc: 'An AI-powered browser extension installed by 12 users has access to SSO session tokens and can read page content across all corporate web applications. The extension is not on the approved software list.'
}, {
  title: 'Unregistered Machine Identity Linked to AI Orchestration Platform',
  category: 'Shadow AI & Agentic Access',
  desc: 'A service account used by an AI orchestration platform lacks required tags (Owner, Purpose, DataClassification). The identity was created 45 days ago and has made 2,300+ API calls but is not tracked in the CMDB.'
}];
// Deterministic seeded random for consistent results
function seededRandom(seed: number) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}
function generateFindings(scanId: string, high: number, medium: number, low: number): Finding[] {
  const seed = scanId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const rng = seededRandom(seed);
  const findings: Finding[] = [];
  let counter = 1;
  const resources_aws = ['iam-role-prod-deploy', 'sg-0a1b2c3d4e', 's3-client-data-prod', 'lambda-api-handler', 'rds-prod-primary', 'ec2-i-0abc123', 'elb-prod-web', 'cloudtrail-main', 'kms-key-prod', 'ecs-cluster-main', 'vpc-prod-01', 'ebs-vol-data', 'sns-alerts-prod', 'sqs-processing', 'dynamodb-sessions', 'ecr-repo-app', 'route53-zone-main', 'nat-gw-prod', 'igw-prod-01', 'acm-cert-api'];
  const resources_azure = ['sp-prod-deploy', 'nsg-web-tier', 'st-proddata01', 'kv-prod-secrets', 'vm-web-01', 'aks-cluster-prod', 'sql-prod-db', 'appsvc-api', 'vnet-prod-01', 'agw-prod-waf', 'cosmos-db-main', 'func-processor', 'redis-cache-prod', 'eventhub-ingest', 'adf-pipeline-main', 'acr-prod-images', 'lb-internal-api', 'bastion-prod', 'disk-data-01', 'mi-app-identity'];
  const resources_gcp = ['sa-prod-pipeline', 'fw-allow-internal', 'gcs-data-lake', 'gke-cluster-prod', 'cloudsql-main', 'pubsub-events', 'gcf-api-handler', 'vpc-prod-network', 'gce-vm-worker-01', 'bq-dataset-analytics', 'kms-keyring-prod', 'lb-global-https', 'artifact-registry-app', 'cloud-run-api', 'memorystore-cache'];
  const allResources = [...resources_aws, ...resources_azure, ...resources_gcp];
  const pickResource = () => allResources[Math.floor(rng() * allResources.length)];
  // Generate Critical/High findings
  for (let i = 0; i < high; i++) {
    const template = CRITICAL_FINDINGS[Math.floor(rng() * CRITICAL_FINDINGS.length)];
    const resource = pickResource();
    const suffix = high > CRITICAL_FINDINGS.length ? `-${Math.floor(rng() * 999).toString().padStart(3, '0')}` : '';
    findings.push({
      id: `F-${counter.toString().padStart(4, '0')}`,
      title: template.title + (suffix ? ` (${resource})` : ''),
      severity: i < Math.ceil(high * 0.3) ? 'Critical' : 'High',
      resource: resource + suffix,
      category: template.category,
      description: template.desc
    });
    counter++;
  }
  // Generate Medium findings
  for (let i = 0; i < medium; i++) {
    const template = MEDIUM_FINDINGS[Math.floor(rng() * MEDIUM_FINDINGS.length)];
    const resource = pickResource();
    const suffix = medium > MEDIUM_FINDINGS.length ? `-${Math.floor(rng() * 999).toString().padStart(3, '0')}` : '';
    findings.push({
      id: `F-${counter.toString().padStart(4, '0')}`,
      title: template.title + (suffix ? ` (${resource})` : ''),
      severity: 'Medium',
      resource: resource + suffix,
      category: template.category,
      description: template.desc
    });
    counter++;
  }
  // Generate Low findings
  for (let i = 0; i < low; i++) {
    const template = LOW_FINDINGS[Math.floor(rng() * LOW_FINDINGS.length)];
    const resource = pickResource();
    const suffix = low > LOW_FINDINGS.length ? `-${Math.floor(rng() * 999).toString().padStart(3, '0')}` : '';
    findings.push({
      id: `F-${counter.toString().padStart(4, '0')}`,
      title: template.title + (suffix ? ` (${resource})` : ''),
      severity: 'Low',
      resource: resource + suffix,
      category: template.category,
      description: template.desc
    });
    counter++;
  }
  return findings;
}
const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'Identity & Access':
      return <Users size={14} data-id="element-671" />;
    case 'Credential Management':
      return <Key size={14} data-id="element-672" />;
    case 'Network Security':
      return <Network size={14} data-id="element-673" />;
    case 'Authentication':
      return <Lock size={14} data-id="element-674" />;
    case 'Data Protection':
      return <HardDrive size={14} data-id="element-675" />;
    case 'Monitoring':
      return <Eye size={14} data-id="element-676" />;
    case 'Governance':
      return <FileWarning size={14} data-id="element-677" />;
    case 'Workload Security':
      return <ShieldAlert size={14} data-id="element-678" />;
    case 'Shadow AI & Agentic Access':
      return <Bot size={14} data-id="element-679" />;
    default:
      return <Server size={14} data-id="element-680" />;
  }
};
export function ScanResultsModal({
  isOpen,
  onClose,
  scan
}: ScanResultsModalProps) {
  const [severityFilter, setSeverityFilter] = useState<'all' | 'Critical' | 'High' | 'Medium' | 'Low'>('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleCount, setVisibleCount] = useState(30);
  const allFindings = useMemo(() => {
    if (!scan || scan.status !== 'Completed') return [];
    return generateFindings(scan.id, scan.findings.high, scan.findings.medium, scan.findings.low);
  }, [scan]);
  if (!isOpen || !scan) return null;
  const totalFindings = scan.findings.high + scan.findings.medium + scan.findings.low;
  const criticalCount = allFindings.filter(f => f.severity === 'Critical').length;
  const highCount = allFindings.filter(f => f.severity === 'High').length;
  const categories = [...new Set(allFindings.map(f => f.category))].sort();
  const filteredFindings = allFindings.filter(f => {
    if (severityFilter !== 'all' && f.severity !== severityFilter) return false;
    if (categoryFilter !== 'all' && f.category !== categoryFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return f.title.toLowerCase().includes(q) || f.resource.toLowerCase().includes(q) || f.category.toLowerCase().includes(q);
    }
    return true;
  });
  const visibleFindings = filteredFindings.slice(0, visibleCount);
  const hasMore = visibleCount < filteredFindings.length;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-681">
      <div className="w-full max-w-[900px] max-h-[90vh] bg-astro-card border border-astro-border rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 flex flex-col" data-id="element-682">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-astro-border shrink-0" data-id="element-683">
          <div data-id="element-684">
            <h2 className="text-lg font-bold text-white" data-id="element-685">{scan.name}</h2>
            <div className="flex items-center gap-3 mt-1 text-xs text-astro-text-secondary" data-id="element-686">
              <span className="flex items-center gap-1.5" data-id="element-687">
                <Clock size={12} data-id="element-688" /> {scan.started}
              </span>
              <span className="w-1 h-1 rounded-full bg-astro-border" data-id="element-689"></span>
              <span data-id="element-690">Duration: {scan.duration}</span>
              <span className="w-1 h-1 rounded-full bg-astro-border" data-id="element-691"></span>
              <span data-id="element-692">Target: {scan.target}</span>
              <span className="w-1 h-1 rounded-full bg-astro-border" data-id="element-693"></span>
              <span className="font-mono" data-id="element-694">{scan.id}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-astro-text-secondary hover:text-white transition-colors p-1" data-id="element-695">
            <X size={20} data-id="element-696" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-6 space-y-5" data-id="element-697">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-3" data-id="element-698">
            <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-4 text-center" data-id="element-699">
              <div className="text-2xl font-mono font-bold text-white mb-1" data-id="element-700">
                {totalFindings.toLocaleString()}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-astro-text-secondary font-bold" data-id="element-701">
                Total
              </div>
            </div>
            <div className="bg-[#0a0e14] border border-red-500/20 rounded-lg p-4 text-center" data-id="element-702">
              <div className="text-2xl font-mono font-bold text-red-400 mb-1" data-id="element-703">
                {criticalCount + highCount}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-astro-text-secondary font-bold" data-id="element-704">
                Critical / High
              </div>
            </div>
            <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-4 text-center" data-id="element-705">
              <div className="text-2xl font-mono font-bold text-yellow-400 mb-1" data-id="element-706">
                {scan.findings.medium}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-astro-text-secondary font-bold" data-id="element-707">
                Medium
              </div>
            </div>
            <div className="bg-[#0a0e14] border border-astro-border rounded-lg p-4 text-center" data-id="element-708">
              <div className="text-2xl font-mono font-bold text-astro-text-secondary mb-1" data-id="element-709">
                {scan.findings.low}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-astro-text-secondary font-bold" data-id="element-710">
                Low
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3" data-id="element-711">
            <div className="relative flex-1 min-w-[200px]" data-id="element-712">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-astro-text-secondary" data-id="element-713" />

              <input type="text" placeholder="Search findings..." value={searchQuery} onChange={e => {
              setSearchQuery(e.target.value);
              setVisibleCount(30);
            }} className="w-full bg-[#0a0e14] border border-astro-border rounded pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors" data-id="element-714" />
            </div>
            <select value={severityFilter} onChange={e => {
            setSeverityFilter(e.target.value as any);
            setVisibleCount(30);
          }} className="bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors appearance-none pr-8" data-id="element-715">
              <option value="all" data-id="element-716">All Severities</option>
              <option value="Critical" data-id="element-717">Critical</option>
              <option value="High" data-id="element-718">High</option>
              <option value="Medium" data-id="element-719">Medium</option>
              <option value="Low" data-id="element-720">Low</option>
            </select>
            <select value={categoryFilter} onChange={e => {
            setCategoryFilter(e.target.value);
            setVisibleCount(30);
          }} className="bg-[#0a0e14] border border-astro-border rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-astro-green/50 transition-colors appearance-none pr-8" data-id="element-721">
              <option value="all" data-id="element-722">All Categories</option>
              {categories.map(cat => <option key={cat} value={cat} data-id="element-723">
                  {cat}
                </option>)}
            </select>
            <span className="text-xs text-astro-text-secondary" data-id="element-724">
              {filteredFindings.length.toLocaleString()} result
              {filteredFindings.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Findings List */}
          {scan.status !== 'Completed' ? <div className="text-center py-12 text-astro-text-secondary" data-id="element-725">
              <Shield size={32} className="mx-auto mb-3 opacity-40" data-id="element-726" />
              <p className="text-sm" data-id="element-727">
                {scan.status === 'Running' ? 'Scan is currently in progress…' : 'Scan failed — no findings available.'}
              </p>
            </div> : filteredFindings.length === 0 ? <div className="text-center py-12 text-astro-text-secondary" data-id="element-728">
              <Search size={32} className="mx-auto mb-3 opacity-40" data-id="element-729" />
              <p className="text-sm" data-id="element-730">No findings match your filters.</p>
            </div> : <>
              <div className="space-y-2" data-id="element-731">
                {visibleFindings.map(finding => <div key={finding.id} className="bg-[#0a0e14] border border-astro-border rounded-lg p-4" data-id="element-732">
                    <div className="flex items-start justify-between gap-3 mb-2" data-id="element-733">
                      <div className="flex items-center gap-2 flex-wrap" data-id="element-734">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border shrink-0 ${finding.severity === 'Critical' ? 'bg-red-500/15 text-red-400 border-red-500/25' : finding.severity === 'High' ? 'bg-astro-danger/10 text-astro-danger border-astro-danger/20' : finding.severity === 'Medium' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' : 'bg-white/5 text-astro-text-secondary/60 border-white/5'}`} data-id="element-735">
                          {finding.severity}
                        </span>
                        <h4 className="text-sm font-bold text-white" data-id="element-736">
                          {finding.title}
                        </h4>
                      </div>
                      <span className="text-[10px] font-mono text-astro-text-secondary/50 shrink-0" data-id="element-737">
                        {finding.id}
                      </span>
                    </div>
                    <p className="text-xs text-astro-text-secondary/80 leading-relaxed mb-3" data-id="element-738">
                      {finding.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-astro-text-secondary" data-id="element-739">
                      <span className="flex items-center gap-1.5" data-id="element-740">
                        {getCategoryIcon(finding.category)}
                        {finding.category}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-astro-border" data-id="element-741"></span>
                      <span className="font-mono" data-id="element-742">{finding.resource}</span>
                    </div>
                  </div>)}
              </div>

              {hasMore && <button onClick={() => setVisibleCount(prev => prev + 50)} className="w-full py-3 text-sm font-medium text-astro-green border border-astro-green/20 rounded-lg hover:bg-astro-green/5 transition-colors" data-id="element-743">
                  Load more ({filteredFindings.length - visibleCount} remaining)
                </button>}
            </>}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-5 border-t border-astro-border shrink-0" data-id="element-744">
          <button className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-astro-green border border-astro-green/30 rounded hover:bg-astro-green/10 transition-colors" data-id="element-745">
            <Download size={14} data-id="element-746" />
            Export Report
          </button>
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-astro-text-secondary border border-astro-border rounded hover:text-white hover:border-white/30 transition-colors" data-id="element-747">
            Close
          </button>
        </div>
      </div>
    </div>;
}